/**
 * Utilities for converting between plain text and suneditor's HTML format,
 * and for performing paragraph-level splicing in stored field HTML.
 *
 * These live in the ai-assistant lib because they exist solely to support
 * AI redaction. VisitFormContext only needs to expose raw HTML in/out.
 */

/**
 * Strips HTML tags and decodes entities to get the plain text content of a
 * visit field. Used when sending field content to the AI redaction backend.
 *
 * <br> and </p> are converted to \n so multi-paragraph content round-trips
 * correctly through the backend.
 */

export function htmlToPlainText(html: string): string {
   return (
      html
         // eslint-disable-next-line require-unicode-regexp
         .replace(/<br\s*\/?>/gi, '\n')
         // eslint-disable-next-line require-unicode-regexp
         .replace(/<\/p>/gi, '\n')
         // eslint-disable-next-line require-unicode-regexp
         .replace(/<[^>]+>/g, '')
         // eslint-disable-next-line require-unicode-regexp
         .replace(/&amp;/g, '&')
         // eslint-disable-next-line require-unicode-regexp
         .replace(/&lt;/g, '<')
         // eslint-disable-next-line require-unicode-regexp
         .replace(/&gt;/g, '>')
         // eslint-disable-next-line require-unicode-regexp
         .replace(/&nbsp;/g, ' ')
         // eslint-disable-next-line require-unicode-regexp
         .replace(/&quot;/g, '"')
         // eslint-disable-next-line require-unicode-regexp
         .replace(/\n{3,}/g, '\n\n')
         .trim()
   );
}

/**
 * Converts a plain-text string (possibly multi-line) to suneditor HTML.
 *
 * Each \n-separated line becomes a <p> element. Empty lines produce
 * <p><br></p> to preserve blank-line spacing as suneditor expects.
 */
export function plainTextToHtml(text: string): string {
   return text
      .split('\n')
      .map((line) => {
         // eslint-disable-next-line require-unicode-regexp
         const escaped = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
         return escaped.length > 0 ? `<p>${escaped}</p>` : '<p><br></p>';
      })
      .join('');
}

/**
 * Replaces paragraphs [startIndex..endIndex] (0-based, inclusive) in the
 * field HTML with replacementHtml, preserving all other paragraphs.
 *
 * Returns null if the indices are out of range.
 */
export function replaceParagraphRange(
   html: string,
   startIndex: number,
   endIndex: number,
   replacementHtml: string
): string | null {
   // eslint-disable-next-line require-unicode-regexp
   const paragraphPattern = /<p(?:\s[^>]*)?>[\s\S]*?<\/p>/gi;
   const paragraphs: Array<{ start: number; end: number }> = [];
   // eslint-disable-next-line init-declarations
   let match: RegExpExecArray | null;

   // eslint-disable-next-line no-cond-assign
   while ((match = paragraphPattern.exec(html)) !== null) {
      paragraphs.push({ start: match.index, end: match.index + match[0].length });
   }

   if (startIndex < 0 || endIndex >= paragraphs.length || startIndex > endIndex) {
      return null;
   }

   return html.slice(0, paragraphs[startIndex].start) + replacementHtml + html.slice(paragraphs[endIndex].end);
}
